import { type ComponentProps, useState } from 'react';
import { faFloppyDisk, faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Form, router } from '@inertiajs/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cmsReviewPath, cmsReviewsPath } from '@/lib/routes';
import { defaultFormData } from '../helpers';
import type {
  AddReviewFormData,
  AddReviewFormErrors,
  AttorneyOption,
  EditableReviewRow,
} from '../types';
import { ReviewFormFields, requiredReviewFieldsFilled } from './review-form-fields';

function buildInitialFormData(
  review: EditableReviewRow,
  attorneys: AttorneyOption[],
): AddReviewFormData {
  return {
    attorneyId: review.attorneyId ?? '',
    attorneyName:
      attorneys.find((attorney) => attorney.id === review.attorneyId)?.name ||
      review.attorneyName ||
      '',
    reviewerName: review.reviewerName ?? '',
    reviewerEmail: review.reviewerEmail ?? '',
    title: review.title,
    description: review.body,
    reviewSummary: review.reviewSummary ?? '',
    rating: review.rating ?? null,
  };
}

export function ReviewForm({
  attorneys,
  mode,
  review,
  onOpenChange,
  onSuccess,
  options,
}: {
  attorneys: AttorneyOption[];
  mode: 'create' | 'edit';
  review?: EditableReviewRow;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  options?: ComponentProps<typeof Form>['options'];
}) {
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState<AddReviewFormData>(() =>
    isEdit && review ? buildInitialFormData(review, attorneys) : defaultFormData,
  );

  const updateField = <K extends keyof AddReviewFormData>(key: K, value: AddReviewFormData[K]) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const handleAttorneySelect = ({
    attorneyId,
    attorneyName,
  }: {
    attorneyId: string;
    attorneyName: string;
  }) => {
    setFormData((current) => ({ ...current, attorneyId, attorneyName }));
  };

  const requiredFilled = requiredReviewFieldsFilled(formData);

  return (
    <Form
      options={options}
      method={isEdit ? 'patch' : 'post'}
      action={isEdit && review ? cmsReviewPath(review.id) : cmsReviewsPath()}
      onSuccess={() => {
        if (isEdit) {
          toast.success('Review updated.');
        } else {
          toast.success(
            <span className="flex items-center gap-2">
              <span>Review added to site.</span>
              <Button type="button" variant="link" onClick={() => router.visit(cmsReviewsPath())}>
                View My Reviews
              </Button>
            </span>,
          );
        }
        onSuccess?.();
        onOpenChange(false);
      }}
      className="space-y-4"
    >
      {({ processing, errors }: { processing: boolean; errors: AddReviewFormErrors }) => (
        <>
          <ReviewFormFields
            attorneys={attorneys}
            formData={formData}
            errors={errors}
            onFieldChange={updateField}
            onAttorneySelect={handleAttorneySelect}
          />

          {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              className="gap-2"
              isLoading={processing}
              disabled={!requiredFilled || processing}
            >
              <Icon icon={isEdit ? faFloppyDisk : faPlus} />
              {isEdit
                ? processing
                  ? 'Saving...'
                  : 'Save Review'
                : processing
                  ? 'Adding to Site...'
                  : 'Add to Site'}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
}
